import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useSearch } from 'wouter';

import useSearchQuery from '../../hooks/useSearch';
import { usePageViewTracking } from '../../hooks/useTracking';

import UrbaniaButton from '../buttons/UrbaniaButton';
import SearchField from '../fields/SearchField';
import AuthorsList from '../lists/AuthorsList';
import CollectionsList from '../lists/CollectionsList';
import DocumentsList from '../lists/DocumentsList';
import SectionTitle from '../typography/SectionTitle';

import styles from '../../styles/pages/search-page.module.css';

type SearchPageProps = {
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    current?: boolean;
    className?: string | null;
    contentClassName?: string | null;
};

function SearchPage({
    entered = false,
    entering = false,
    leaving = false,
    current = false,
    className = null,
    contentClassName = null,
}: SearchPageProps) {
    const url = useUrlGenerator();
    const [, setLocation] = useLocation();
    const intl = useIntl();
    const search = useSearch();
    const { q: queryParam = null } = queryString.parse(search);
    const [query, setQuery] = useState(queryParam);

    const {
        items: searchResults = null,
        isFetching: isFetchingResults,
        searchQuery,
        // isFetched = false,
        update,
    } = useSearchQuery(query, {
        count: 20,
    });
    const onSubmit = (e) => {
        e.preventDefault();
        update();
    };
    const onFieldChange = (newValue) => {
        setQuery(newValue !== null && newValue.length > 0 ? newValue : null);
    };

    useEffect(() => {
        if (searchQuery === null || searchQuery.length === 0) {
            setLocation(url('search'));
        } else {
            setLocation(`${url('search')}?${queryString.stringify({ q: searchQuery })}`);
        }
    }, [searchQuery]);

    useEffect(() => {
        if (queryParam !== searchQuery) {
            setQuery(queryParam);
        }
    }, [queryParam]);

    usePageViewTracking(current, {
        pageType: 'search',
    });

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.entering]: entering,
                    [styles.leaving]: leaving,
                },
                className,
                contentClassName,
            ])}
        >
            <UrbaniaButton className={styles.urbania} />
            <div className={styles.inner}>
                <form className={styles.form} onSubmit={onSubmit}>
                    <SectionTitle className={styles.title}>
                        <FormattedMessage
                            defaultMessage="Recherche sur le site"
                            description="Page title"
                        />
                    </SectionTitle>
                    <SearchField
                        value={query}
                        placeholder={intl.formatMessage({
                            defaultMessage: 'Recherche...',
                            description: 'Field placeholder',
                        })}
                        loading={isFetchingResults}
                        onChange={onFieldChange}
                        className={styles.field}
                    />
                </form>

                {searchResults !== null && query !== null ? (
                    searchResults.map(({ id, label, data }) => (
                        <div className={styles.results} key={`results-${id}`}>
                            <SectionTitle className={styles.sectionTitle}>{label}</SectionTitle>
                            {id === 'authors' ? (
                                <AuthorsList
                                    items={data}
                                    withoutLoader
                                    cardType="horizontal"
                                    className={styles.list}
                                    itemsClassName={styles.items}
                                />
                            ) : null}
                            {id === 'collections' ? (
                                <CollectionsList
                                    items={data}
                                    withoutLoader
                                    cardType="vertical"
                                    cardTheme="box-translucent"
                                    cardWith={['brand-icon']}
                                    className={styles.list}
                                    minColumns={2}
                                    cardWidth={150}
                                    itemsClassName={styles.items}
                                />
                            ) : null}
                            {id === 'articles' ? (
                                <DocumentsList
                                    items={data}
                                    withoutLoader
                                    cardType="vertical"
                                    cardTheme="box-translucent"
                                    cardWith={['brand-icon']}
                                    className={styles.list}
                                    minColumns={2}
                                    cardWidth={150}
                                    itemsClassName={styles.items}
                                />
                            ) : null}
                        </div>
                    ))
                ) : (
                    <DocumentsList
                        // query={{
                        //     popular: 'week',
                        // }}
                        hook="popular"
                        presentation="layout"
                        layout="1_4_1_4"
                        count={10}
                        cardType="horizontal"
                        cardTheme="box-translucent"
                        cardWith={['brand-icon']}
                        className={styles.list}
                        itemsClassName={styles.items}
                    />
                )}
            </div>
        </div>
    );
}

export default SearchPage;
